// Heuristic date detection + formatting for chart axes, tooltips,
// and table cells. Two formatters: a compact one for chart axes
// (drops year on single-year ranges to keep tick labels readable),
// and a full one for tooltips and tables (always includes year so
// the date is unambiguous when read out of context).

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2})?/;
const SHORT_MONTH_DATE = /^[A-Z][a-z]{2}-?\d{1,2}[- ]\d{4}/;

export function looksLikeDates(values: ReadonlyArray<unknown>): boolean {
  let hits = 0;
  let checked = 0;
  for (const v of values) {
    if (typeof v !== "string") continue;
    checked++;
    if (ISO_DATE.test(v) || SHORT_MONTH_DATE.test(v)) hits++;
    if (checked >= 8) break;
  }
  return checked > 0 && hits / checked >= 0.7;
}

function formatDate(value: unknown, includeYear: boolean): string {
  if (typeof value !== "string") return String(value ?? "");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return includeYear
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Compact axis-label formatter. Drops the year for single-year ranges. */
export function dateAxisFormatter(values: ReadonlyArray<unknown>) {
  if (!looksLikeDates(values)) return undefined;
  const years = new Set<number>();
  for (const v of values) {
    if (typeof v !== "string") continue;
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) years.add(d.getFullYear());
  }
  const includeYear = years.size > 1;
  return (value: unknown) => formatDate(value, includeYear);
}

/** Always-full-year formatter for tooltips and table cells. */
export function dateFullFormatter(values: ReadonlyArray<unknown>) {
  if (!looksLikeDates(values)) return undefined;
  return (value: unknown) => formatDate(value, true);
}
